import { useParams, Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { getPostBySlug } from '@/data/posts'
import SEO from '@/components/SEO'
import StructuredData from '@/components/StructuredData'

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-US', {
        year:  'numeric',
        month: 'long',
        day:   'numeric',
    })
}

const TAG_COLORS: Record<string, string> = {
    community:     '#00FF88',
    stem:          '#00FF88',
    outreach:      '#00FF88',
    education:     '#00D9FF',
    mentorship:    '#00D9FF',
    python:        '#FFB300',
    'open-source': '#FFB300',
    cli:           '#FFB300',
    automation:    '#FFB300',
    pypi:          '#FFB300',
    backend:       '#C084FC',
    api:           '#C084FC',
    fastapi:       '#C084FC',
    engineering:   '#C084FC',
    typescript:    '#3178C6',
    'vs-code':     '#007ACC',
    arduino:       '#00979D',
    extensions:    '#00979D',
}

function tagColor(tag: string) {
    return TAG_COLORS[tag] ?? '#555555'
}

export default function BlogPostPage() {
    const { slug } = useParams<{ slug: string }>()
    const post = slug ? getPostBySlug(slug) : undefined

    if (!post) return <Navigate to="/blog" replace />

    return (
        <div className="min-h-screen font-mono py-12">
            <SEO
                title={`${post.title} | Rapheal Kwabena`}
                description={post.excerpt}
                keywords={post.tags.join(', ')}
                canonical={`https://raphealgadoh.me/blog/${post.slug}`}
                ogType="article"
                publishedTime={post.date}
            />
            <StructuredData
                type="Article"
                headline={post.title}
                description={post.excerpt}
                datePublished={post.date}
                url={`https://raphealgadoh.me/blog/${post.slug}`}
            />
            <div className="max-w-3xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="space-y-10"
                >

                    {/* ── Breadcrumb ── */}
                    <div className="text-xs text-[#444] flex items-center gap-2">
                        <Link to="/blog" className="hover:text-primary transition-colors">blog/</Link>
                        <span className="text-[#2a2a2a]">/</span>
                        <span className="text-foreground">{post.slug}</span>
                    </div>

                    {/* ── Post header ── */}
                    <div className="space-y-3 border-b border-[#111] pb-8">
                        <div className="flex items-center gap-3 text-[10px] text-[#444]">
                            <span>{formatDate(post.date)}</span>
                            <span className="text-[#2a2a2a]">—</span>
                            <span>{post.readingTime}</span>
                        </div>

                        <h1 className="text-lg sm:text-xl text-foreground leading-snug">
                            <span className="text-primary">&gt; </span>
                            {post.title}
                        </h1>

                        <p className="text-xs text-[#555] leading-relaxed">
                            {post.excerpt}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 pt-1">
                            {post.tags.map(tag => (
                                <span
                                    key={tag}
                                    className="text-[9px] font-mono px-1.5 py-0.5 border rounded-sm"
                                    style={{
                                        color:       tagColor(tag),
                                        borderColor: `${tagColor(tag)}30`,
                                        background:  `${tagColor(tag)}08`,
                                    }}
                                >
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* ── Post body ── */}
                    <article className="prose-terminal">
                        <ReactMarkdown
                            components={{
                                h2: ({ children }) => (
                                    <h2 className="text-sm text-primary font-mono mt-8 mb-3 flex items-center gap-2">
                                        <span className="text-[#333]">##</span> {children}
                                    </h2>
                                ),
                                h3: ({ children }) => (
                                    <h3 className="text-xs text-foreground font-mono mt-6 mb-2 flex items-center gap-2">
                                        <span className="text-[#333]">###</span> {children}
                                    </h3>
                                ),
                                p: ({ children }) => (
                                    <p className="text-xs text-[#888] leading-relaxed mb-4">
                                        {children}
                                    </p>
                                ),
                                ul: ({ children }) => (
                                    <ul className="space-y-1.5 mb-4 ml-2">
                                        {children}
                                    </ul>
                                ),
                                li: ({ children }) => (
                                    <li className="text-xs text-[#888] flex gap-2 leading-relaxed">
                                        <span className="text-primary shrink-0">›</span>
                                        <span>{children}</span>
                                    </li>
                                ),
                                strong: ({ children }) => (
                                    <strong className="text-foreground font-semibold">
                                        {children}
                                    </strong>
                                ),
                                em: ({ children }) => (
                                    <em className="text-[#777] not-italic border-l border-primary/30 pl-2">
                                        {children}
                                    </em>
                                ),
                                blockquote: ({ children }) => (
                                    <blockquote className="border-l-2 border-primary/40 pl-4 my-4 text-xs text-[#666] italic">
                                        {children}
                                    </blockquote>
                                ),
                                code: ({ children, className }) => {
                                    const isBlock = className?.startsWith('language-')
                                    if (isBlock) {
                                        return (
                                            <code className="block text-xs text-primary/90 bg-[#0a0a0a] border border-[#1a1a1a] rounded-sm p-4 overflow-x-auto mb-4 font-mono leading-relaxed">
                                                {children}
                                            </code>
                                        )
                                    }
                                    return (
                                        <code className="text-primary/80 bg-[#0f0f0f] border border-[#1a1a1a] px-1 py-0.5 rounded-sm text-[11px] font-mono">
                                            {children}
                                        </code>
                                    )
                                },
                                pre: ({ children }) => (
                                    <pre className="mb-4">{children}</pre>
                                ),
                                table: ({ children }) => (
                                    <div className="overflow-x-auto mb-6">
                                        <table className="w-full text-xs font-mono border-collapse">
                                            {children}
                                        </table>
                                    </div>
                                ),
                                thead: ({ children }) => (
                                    <thead className="border-b border-[#222]">{children}</thead>
                                ),
                                th: ({ children }) => (
                                    <th className="text-left text-[#555] font-normal px-3 py-2 text-[10px]">
                                        {children}
                                    </th>
                                ),
                                td: ({ children }) => (
                                    <td className="text-[#888] px-3 py-2 border-b border-[#111]">
                                        {children}
                                    </td>
                                ),
                                a: ({ href, children }) => (
                                    <a
                                        href={href}
                                        className="text-primary hover:underline"
                                        target={href?.startsWith('http') ? '_blank' : undefined}
                                        rel="noopener noreferrer"
                                    >
                                        {children}
                                    </a>
                                ),
                                hr: () => (
                                    <hr className="border-[#111] my-8" />
                                ),
                            }}
                        >
                            {post.content}
                        </ReactMarkdown>
                    </article>

                    {/* ── Footer nav ── */}
                    <div className="border-t border-[#111] pt-8 flex items-center justify-between">
                        <Link
                            to="/blog"
                            className="text-xs text-[#555] hover:text-primary transition-colors font-mono"
                        >
                            ← back to blog
                        </Link>
                        <Link
                            to="/contact"
                            className="text-xs text-[#555] hover:text-primary transition-colors font-mono"
                        >
                            get in touch →
                        </Link>
                    </div>

                </motion.div>
            </div>
        </div>
    )
}
